/**
 * Text case transformation utilities
 */

/**
 * Converts text to sentence case (capitalizes first letter of each sentence)
 * @param {string} text - The text to transform
 * @returns {string} - The text in sentence case
 */
export const toSentenceCase = text => {
  if (!text) return text;

  // Convert to lowercase first
  let result = text.toLowerCase();

  // Capitalize first letter
  result = result.charAt(0).toUpperCase() + result.slice(1);

  // Capitalize first letter after sentence terminators (. ! ?) followed by space
  result = result.replace(
    /([.!?]\s+)([a-z])/g,
    (match, punctuation, letter) => {
      return punctuation + letter.toUpperCase();
    }
  );

  return result;
};

/**
 * Converts HTML text to sentence case while preserving HTML formatting
 * @param {string} htmlText - The HTML text to transform
 * @returns {string} - The HTML text in sentence case
 */
export const toSentenceCaseHTML = htmlText => {
  if (!htmlText) return htmlText;

  // Create a temporary DOM element to parse HTML
  if (typeof window !== "undefined") {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlText;

    // Function to process text nodes
    const processTextNode = node => {
      if (node.nodeType === Node.TEXT_NODE) {
        node.textContent = toSentenceCase(node.textContent);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        // Recursively process child nodes
        Array.from(node.childNodes).forEach(processTextNode);
      }
    };

    Array.from(tempDiv.childNodes).forEach(processTextNode);

    return tempDiv.innerHTML;
  }

  // Fallback for server-side rendering
  return toSentenceCase(htmlText);
};

/**
 * Applies text case transformation based on the case type
 * @param {string} text - The text to transform
 * @param {string} caseType - The case type: 'uppercase', 'lowercase', 'capitalize', 'sentence'
 * @returns {string} - The transformed text
 */
export const applyTextCase = (text, caseType) => {
  if (!text) return text;

  switch (caseType) {
    case "uppercase":
      return text.toUpperCase();
    case "lowercase":
      return text.toLowerCase();
    case "capitalize":
      // Title case - capitalize first letter of each word
      // Include parentheses and other punctuation as word boundaries, but not apostrophes
      return text
        .toLowerCase()
        .replace(
          /(^|\s|[()[\]{}".,;:!?-])([a-z])/g,
          (match, boundary, letter) => boundary + letter.toUpperCase()
        );
    case "sentence":
      return toSentenceCase(text);
    default:
      return text;
  }
};

/**
 * Applies text case transformation to HTML content while preserving HTML formatting
 * @param {string} htmlText - The HTML text to transform
 * @param {string} caseType - The case type: 'uppercase', 'lowercase', 'capitalize', 'sentence'
 * @returns {string} - The transformed HTML text
 */
export const applyTextCaseHTML = (htmlText, caseType) => {
  if (!htmlText) return htmlText;

  // For sentence case, use special HTML transformation
  if (caseType === "sentence") {
    return toSentenceCaseHTML(htmlText);
  }

  // For other text transformations, process HTML content while preserving tags
  if (typeof window !== "undefined") {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlText;

    // Function to process text nodes
    const processTextNode = node => {
      if (node.nodeType === Node.TEXT_NODE) {
        node.textContent = applyTextCase(node.textContent, caseType);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        // Recursively process child nodes
        Array.from(node.childNodes).forEach(processTextNode);
      }
    };

    Array.from(tempDiv.childNodes).forEach(processTextNode);

    return tempDiv.innerHTML;
  }

  // Fallback for server-side rendering
  return applyTextCase(htmlText, caseType);
};
